import {
  CUSTOMER_PORTAL_FIELDS,
  type CustomerPortalInputs
} from "../../calculators/customerPortal/defaults";
import { customerPortalHelpHtml } from "../../calculators/customerPortal/help";
import { calculateCustomerPortalRoi } from "../../calculators/customerPortal/formula";
import { formatDays, formatWholeCurrency } from "../../lib/currency";
import type { CurrencyCode, PartnerBrand } from "../../lib/types";
import { renderFormField } from "../components/formField";
import { renderGauge } from "../components/gauge";
import { renderHeader } from "../components/header";
import { renderHelpModal } from "../components/helpModal";

interface CustomerPortalPageArgs {
  inputs: CustomerPortalInputs;
  currency: CurrencyCode;
  brand: PartnerBrand;
  helpOpen: boolean;
  animateEntrance: boolean;
}

export function renderCustomerPortalPage({
  inputs,
  currency,
  brand,
  helpOpen,
  animateEntrance
}: CustomerPortalPageArgs): string {
  const result = calculateCustomerPortalRoi(inputs);

  const formFields = CUSTOMER_PORTAL_FIELDS.map((field) =>
    renderFormField({
      name: field.key,
      label: field.label,
      kind: field.kind,
      value: inputs[field.key],
      currency,
      step: field.step
    })
  ).join("");

  return `
    <div class="roi-app ${animateEntrance ? "is-entering" : ""}">
      ${renderHeader(brand, "Customer Portal ROI Calculator")}
      <main class="roi-main">
      <section class="input-section" aria-labelledby="input-assumptions-title">
        <h2 id="input-assumptions-title" class="section-title">Input Assumptions</h2>
        <div class="currency-row">
          <label for="currency">Currency</label>
          <select id="currency" name="currency" data-action="currency-change">
            <option value="BMD" ${currency === "BMD" ? "selected" : ""}>BMD</option>
            <option value="USD" ${currency === "USD" ? "selected" : ""}>USD</option>
            <option value="CAD" ${currency === "CAD" ? "selected" : ""}>CAD</option>
            <option value="EUR" ${currency === "EUR" ? "selected" : ""}>EUR</option>
            <option value="GBP" ${currency === "GBP" ? "selected" : ""}>GBP</option>
          </select>
        </div>
        <div class="field-grid">${formFields}</div>
      </section>
      <section class="result-section customer-layout" aria-labelledby="results-title">
        <h2 id="results-title" class="section-title">Results</h2>
        <article class="result-card">
          <div class="result-icon"><i class="fa-solid fa-users-viewfinder" aria-hidden="true"></i></div>
          <p class="result-value" data-testid="primary-value">${formatWholeCurrency(result.firstYearMonthlyValue, currency)}</p>
          <p class="result-label">First Year Monthly Value</p>
        </article>
        <article class="result-card gauge-card">
          ${renderGauge(result.valuePerDay, result.costPerDay, currency, brand)}
        </article>
        <article class="result-card">
          <div class="result-icon"><i class="fa-solid fa-comments" aria-hidden="true"></i></div>
          <p class="result-value">${formatDays(result.totalDaysSavedPerMonth)} days</p>
          <p class="result-label">Total Monthly CSR Time Saved</p>
        </article>
      </section>
      <footer class="roi-footnotes">
        <span>* based on 21 business days per month and 8 hours per day</span>
        <span>* default values are based on industry averages and standard fees for the Customer Portal solution</span>
      </footer>
      </main>
      <button type="button" class="help-trigger" data-action="open-help" aria-label="Show formula details">?</button>
      ${renderHelpModal(
        "Customer Portal ROI Calculator - Computation",
        customerPortalHelpHtml(inputs, result, currency),
        helpOpen
      )}
    </div>
  `;
}
